import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { extractErrorMessage } from '../lib/error.js';
import type { Firmware } from '../types/firmware.js';

type CreateFirmwareInput = {
  version: string;
  deviceModels: string[];
  file: File;
};

async function fetchFirmwares(): Promise<Firmware[]> {
  const { data } = await apiClient.get<Firmware[]>('/firmwares');
  return data;
}

async function fetchDeviceModels(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>('/devices/models');
  return data;
}

async function createFirmware(input: CreateFirmwareInput): Promise<Firmware> {
  const formData = new FormData();
  formData.append('version', input.version);
  formData.append('file', input.file);
  input.deviceModels.forEach((model) => {
    formData.append('deviceModels[]', model);
  });

  try {
    const { data } = await apiClient.post<Firmware>('/firmwares', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to create firmware'));
  }
}

async function deleteFirmware(id: string): Promise<void> {
  try {
    await apiClient.delete(`/firmwares/${id}`);
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to delete firmware'));
  }
}

export function useFirmwares(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['firmwares'],
    queryFn: fetchFirmwares,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useDeviceModels(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['firmwares', 'device-models'],
    queryFn: fetchDeviceModels,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateFirmware() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFirmware,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmwares'] });
    },
  });
}

export function useDeleteFirmware() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFirmware,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmwares'] });
    },
  });
}

async function fetchLatestFirmwareForDevice(deviceId: string): Promise<Firmware | null> {
  try {
    const { data } = await apiClient.get<Firmware | null>(`/firmwares/devices/${deviceId}/latest`);
    return data;
  } catch {
    return null;
  }
}

async function triggerFirmwareUpdate(firmwareId: string, deviceId: string): Promise<void> {
  try {
    await apiClient.post(`/firmwares/devices/${deviceId}/update`, { firmwareId });
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to trigger firmware update'));
  }
}

export function useLatestFirmwareForDevice(deviceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['firmwares', 'device', deviceId, 'latest'],
    queryFn: () => fetchLatestFirmwareForDevice(deviceId),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useTriggerFirmwareUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ firmwareId, deviceId }: { firmwareId: string; deviceId: string }) =>
      triggerFirmwareUpdate(firmwareId, deviceId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['firmwares', 'device', variables.deviceId, 'latest'],
      });
    },
  });
}
